import React, { useState } from "react";

import type {
  ExcalidrawImperativeAPI,
  LeftToolbarFreedrawVariant,
  LeftToolbarLineVariant,
  LeftToolbarShapeTool,
} from "../types";

export type ExternalLeftToolbarProps = {
  excalidrawAPI: ExcalidrawImperativeAPI | null;
  className?: string;
  style?: React.CSSProperties;
};

const SHAPE_OPTIONS: LeftToolbarShapeTool[] = [
  "rectangle",
  "ellipse",
  "diamond",
  "triangle",
  "pentagon",
  "callout",
  "hollow_arrow",
  "hollow_double_arrow",
];

const LINE_VARIANTS: LeftToolbarLineVariant[] = [
  "line",
  "arrow",
  "doubleArrow",
  "outlineArrow",
  "outlineDoubleArrow",
];

const FREEDRAW_VARIANTS: LeftToolbarFreedrawVariant[] = ["pen", "highlighter"];

export const ExternalLeftToolbar = ({
  excalidrawAPI,
  className,
  style,
}: ExternalLeftToolbarProps) => {
  const [shape, setShape] = useState<LeftToolbarShapeTool>("rectangle");
  const [lineVariant, setLineVariant] = useState<LeftToolbarLineVariant>("line");
  const [freedrawVariant, setFreedrawVariant] =
    useState<LeftToolbarFreedrawVariant>("pen");
  const [strokeWidth, setStrokeWidth] = useState<number>(2);
  const [strokeColor, setStrokeColor] = useState<string>("#1f1f1f");

  const disabled = !excalidrawAPI;

  return (
    <div
      className={className}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: 10,
        border: "1px solid #e5e7eb",
        borderRadius: 10,
        width: 220,
        background: "#fff",
        ...style,
      }}
    >
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => excalidrawAPI?.leftToolbar.selectSelection()}
        >
          Selection
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => excalidrawAPI?.leftToolbar.selectEraser()}
        >
          Eraser
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ minWidth: 70 }}>Freedraw</label>
        <select
          value={freedrawVariant}
          onChange={(e) =>
            setFreedrawVariant(e.target.value as LeftToolbarFreedrawVariant)
          }
        >
          {FREEDRAW_VARIANTS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            excalidrawAPI?.leftToolbar.selectFreedraw({
              variant: freedrawVariant,
              strokeWidth,
              strokeColor,
            })
          }
        >
          Apply
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ minWidth: 70 }}>Line</label>
        <select
          value={lineVariant}
          onChange={(e) =>
            setLineVariant(e.target.value as LeftToolbarLineVariant)
          }
        >
          {LINE_VARIANTS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            excalidrawAPI?.leftToolbar.selectLine({
              variant: lineVariant,
              strokeWidth,
              strokeColor,
            })
          }
        >
          Apply
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ minWidth: 70 }}>Shape</label>
        <select
          value={shape}
          onChange={(e) => setShape(e.target.value as LeftToolbarShapeTool)}
        >
          {SHAPE_OPTIONS.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            excalidrawAPI?.leftToolbar.selectShape(shape, {
              strokeWidth,
              strokeColor,
            })
          }
        >
          Apply
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ minWidth: 70 }}>Text</label>
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            excalidrawAPI?.leftToolbar.selectText({
              strokeColor,
            })
          }
        >
          Text Tool
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ minWidth: 70 }}>Stroke</label>
        <input
          type="number"
          min={1}
          max={20}
          value={strokeWidth}
          onChange={(e) => setStrokeWidth(Number(e.target.value || 1))}
          style={{ width: 60 }}
        />
        <input
          type="color"
          value={strokeColor}
          onChange={(e) => setStrokeColor(e.target.value)}
        />
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            excalidrawAPI?.leftToolbar.updateStyle({
              currentItemStrokeWidth: strokeWidth,
              currentItemStrokeColor: strokeColor,
            })
          }
        >
          Sync
        </button>
      </div>
    </div>
  );
};

